// Copyright (c) Ping. All rights reserved.
//
// First-run recovery popover guide (web). The recovery backup now runs
// silently in the BACKGROUND right after signup, so this short, one-time
// coachmark points the user at Settings to VIEW + SAVE the recovery
// phrase that's already securing their account. Built on
// `@reactour/tour`, themed with the app design tokens (`@ping/ui`
// `tokens` + `useColorScheme`) so the popover matches the shell.
//
// Shown exactly once per device, and only while there's still something
// to save: suppressed when EITHER `recovery.guideSeen` is set OR the
// user has already confirmed they saved the phrase
// (`__recovery.mnemonicSavedConfirmed`, which also purges the stored
// mnemonic). The "seen" marker is stamped on any dismissal (CTA, "Maybe
// later", close, mask).

import { useEffect, useRef, useState } from 'react';

import { TourProvider, useTour } from '@reactour/tour';

import { tokens, useColorScheme } from '@ping/ui';

import {
  markRecoveryGuideSeen,
  readRecoveryGuideSeen,
} from '@features/auth/RecoveryBackupSetupHost';
import { readRecoveryMnemonicConfirmed } from '@features/auth/recoveryMnemonicStore';
import { readTourSeen, TOUR_HOME, useOnboardingCoordinator } from '@features/onboarding/tour';
import { useRouter } from '@shell/RouterContext';

// The recovery nudge shares the screen with the product tours through the
// onboarding coordinator, so only one first-run guide is ever visible. It also
// holds back until the app intro tour is finished, so a brand-new user always
// gets the walkthrough first and the recovery nudge second.
const RECOVERY_TOUR_ID = 'recovery';
const RECOVERY_TOUR_PRIORITY = 10;

// The Settings modal is opened from the account avatar at the bottom of
// the left nav-rail (the redesigned rail dropped the standalone Settings
// button; `onAccountPress` → `router.openModal('settings')`). The shared
// `@ping/ui` rail renders the avatar with `testID="nav-account"`, so the
// guide anchors here — pointing at the real trigger for recovery setup.
const SETTINGS_SELECTOR = '[data-testid="nav-account"]';

interface Palette {
  readonly surface: string;
  readonly border: string;
  readonly textPrimary: string;
  readonly textSecondary: string;
  readonly accent: string;
  readonly onAccent: string;
  readonly radius: number;
  readonly radiusSm: number;
  readonly maskOverlay: string;
}

function paletteFor(scheme: 'light' | 'dark'): Palette {
  const c = tokens.color.semantic[scheme];
  return {
    surface: c.surface.primary,
    border: c.border.subtle,
    textPrimary: c.text.primary,
    textSecondary: c.text.secondary,
    accent: c.accent.brand,
    onAccent: c.text.onAccent,
    radius: tokens.radius.lg,
    radiusSm: tokens.radius.sm,
    // Scrim behind the highlight — slightly heavier in dark so the
    // dimmed shell reads as inactive.
    maskOverlay: scheme === 'dark' ? 'rgba(0,0,0,0.6)' : 'rgba(15,23,42,0.45)',
  };
}

function RecoveryGuideStep({
  palette,
  onSetUp,
  onLater,
}: {
  readonly palette: Palette;
  readonly onSetUp: () => void;
  readonly onLater: () => void;
}) {
  return (
    <div style={{ maxWidth: 320 }}>
      <h2
        style={{
          margin: '0 0 8px',
          fontSize: 17,
          fontWeight: 700,
          color: palette.textPrimary,
        }}
      >
        Save your recovery phrase
      </h2>
      <p
        style={{
          margin: '0 0 18px',
          fontSize: 14,
          lineHeight: 1.5,
          color: palette.textSecondary,
        }}
      >
        Your account is already backed up. Write down your 12-word recovery phrase from Settings so
        you can restore your account if you ever lose access to your devices.
      </p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onLater}
          style={{
            padding: '8px 14px',
            fontSize: 14,
            fontWeight: 600,
            borderRadius: palette.radiusSm,
            border: `1px solid ${palette.border}`,
            background: 'transparent',
            color: palette.textPrimary,
            cursor: 'pointer',
          }}
        >
          Maybe later
        </button>
        <button
          type="button"
          onClick={onSetUp}
          style={{
            padding: '8px 14px',
            fontSize: 14,
            fontWeight: 600,
            borderRadius: palette.radiusSm,
            border: 'none',
            background: palette.accent,
            color: palette.onAccent,
            cursor: 'pointer',
          }}
        >
          View phrase
        </button>
      </div>
    </div>
  );
}

// Auto-starts the guide once, after the shell has mounted. Lives inside
// the provider so it can drive `useTour`.
function RecoveryGuideAutoStart({ palette }: { readonly palette: Palette }): null {
  const { setIsOpen, setSteps } = useTour();
  const router = useRouter();
  const active = useOnboardingCoordinator((s) => s.active);
  const request = useOnboardingCoordinator((s) => s.request);
  const release = useOnboardingCoordinator((s) => s.release);
  const [eligible, setEligible] = useState(false);
  const [homeTourDone, setHomeTourDone] = useState(false);
  const openedRef = useRef(false);

  // Decide once whether there is anything left to surface.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [seen, confirmed] = await Promise.all([
          readRecoveryGuideSeen(),
          readRecoveryMnemonicConfirmed(),
        ]);
        // Suppress once the user has seen the guide, or has already
        // saved + confirmed their phrase (nothing left to surface).
        if (!cancelled && !seen && !confirmed) {
          setEligible(true);
        }
      } catch {
        /* guide is best-effort — never block the shell */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Hold back until the app intro tour is finished. Re-checked whenever the
  // coordinator frees up (which is when the intro tour releases the screen),
  // so this flips true the moment the intro completes or is skipped.
  useEffect(() => {
    if (homeTourDone) {
      return;
    }
    let cancelled = false;
    void (async () => {
      const seen = await readTourSeen(TOUR_HOME);
      if (!cancelled && seen) {
        setHomeTourDone(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active, homeTourDone]);

  const shouldShow = eligible && homeTourDone;

  // Queue for the screen once we know it should show.
  useEffect(() => {
    if (shouldShow) {
      request(RECOVERY_TOUR_ID, RECOVERY_TOUR_PRIORITY);
    }
  }, [shouldShow, request]);

  // Open when the coordinator grants the slot (after any higher-priority
  // guide, like the app intro tour, has finished).
  useEffect(() => {
    if (!shouldShow || active !== RECOVERY_TOUR_ID || openedRef.current) {
      return;
    }
    openedRef.current = true;
    const dismiss = (): void => {
      void markRecoveryGuideSeen();
      release(RECOVERY_TOUR_ID);
      setIsOpen(false);
    };
    const setUp = (): void => {
      void markRecoveryGuideSeen();
      release(RECOVERY_TOUR_ID);
      setIsOpen(false);
      router.openModal('settings');
    };
    // Anchor the popover to the account avatar (opens Settings);
    // reactour highlights it and auto-flips side if `right` doesn't fit.
    setSteps?.([
      {
        selector: SETTINGS_SELECTOR,
        position: 'right',
        padding: { mask: 6 },
        content: <RecoveryGuideStep palette={palette} onSetUp={setUp} onLater={dismiss} />,
      },
    ]);
    setIsOpen(true);
  }, [shouldShow, active, release, setIsOpen, setSteps, router, palette]);

  return null;
}

export function RecoveryGuideProvider({
  children,
}: {
  readonly children: React.ReactNode;
}): JSX.Element {
  const palette = paletteFor(useColorScheme() === 'dark' ? 'dark' : 'light');

  return (
    <TourProvider
      steps={[]}
      // Dismissing via the close button or the mask still counts as
      // "seen" so the guide doesn't reappear on the next load.
      onClickClose={({ setIsOpen }) => {
        void markRecoveryGuideSeen();
        useOnboardingCoordinator.getState().release(RECOVERY_TOUR_ID);
        setIsOpen(false);
      }}
      onClickMask={({ setIsOpen }) => {
        void markRecoveryGuideSeen();
        useOnboardingCoordinator.getState().release(RECOVERY_TOUR_ID);
        setIsOpen(false);
      }}
      showNavigation={false}
      showBadge={false}
      showDots={false}
      // Theme `.reactour__popover` + mask + close with the app tokens so
      // the guide matches the shell in both light and dark.
      styles={{
        popover: (base) => ({
          ...base,
          background: palette.surface,
          color: palette.textPrimary,
          borderRadius: palette.radius,
          border: `1px solid ${palette.border}`,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
          padding: 20,
          maxWidth: 360,
        }),
        maskArea: (base) => ({ ...base, rx: 14 }),
        maskWrapper: (base) => ({ ...base, color: palette.maskOverlay }),
        close: (base) => ({
          ...base,
          color: palette.textSecondary,
          top: 14,
          right: 14,
          width: 10,
          height: 10,
        }),
      }}
    >
      <RecoveryGuideAutoStart palette={palette} />
      {children}
    </TourProvider>
  );
}
